import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { CreditCard, CheckCircle2, Clock, AlertTriangle, Loader2, XCircle } from 'lucide-react';

import { useAuthStore } from '@/store/auth.store';
import { PageHeader, Spinner, SectionCard } from '@/components/fields';
import { getMe, type SubStatus } from '@/services/profile';
import {
  getBilling,
  checkout,
  cancelSubscription,
  type PaymentStatus,
} from '@/services/billing';

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function daysLeft(iso: string | null): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return ms <= 0 ? 0 : Math.ceil(ms / 86_400_000);
}

const rub = (kop: number) => (kop / 100).toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + ' ₽';

const meta: Record<SubStatus, { label: string; cls: string; icon: typeof Clock }> = {
  TRIAL: { label: 'Пробный период', cls: 'bg-amber-50 text-amber-700', icon: Clock },
  ACTIVE: { label: 'Активна', cls: 'bg-emerald-50 text-emerald-700', icon: CheckCircle2 },
  PAST_DUE: { label: 'Просрочена оплата', cls: 'bg-red-50 text-red-700', icon: AlertTriangle },
  CANCELLED: { label: 'Отменена', cls: 'bg-ink/10 text-ink/60', icon: AlertTriangle },
};

const PAYMENT_BADGE: Record<PaymentStatus, { label: string; cls: string }> = {
  PENDING: { label: 'В обработке', cls: 'bg-amber-50 text-amber-700' },
  SUCCEEDED: { label: 'Оплачен', cls: 'bg-emerald-50 text-emerald-700' },
  FAILED: { label: 'Ошибка', cls: 'bg-red-50 text-red-700' },
  REFUNDED: { label: 'Возврат', cls: 'bg-ink/10 text-ink/60' },
};

export default function Subscription() {
  const qc = useQueryClient();
  const setSupplier = useAuthStore((s) => s.setSupplier);
  const [params, setParams] = useSearchParams();
  const [redirecting, setRedirecting] = useState(false);

  const { data, isLoading } = useQuery({ queryKey: ['billing'], queryFn: getBilling });

  // Возврат из ЮKassa (?paid=1): обновляем биллинг и профиль (статус в шапке).
  useEffect(() => {
    if (params.get('paid') !== '1') return;
    toast.success('Оплата обрабатывается — статус обновится в течение минуты');
    qc.invalidateQueries({ queryKey: ['billing'] });
    getMe().then(setSupplier).catch(() => {});
    params.delete('paid');
    setParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pay = useMutation({
    mutationFn: () => checkout(`${window.location.origin}/subscription?paid=1`),
    onSuccess: (res) => {
      setRedirecting(true);
      window.location.href = res.confirmationUrl;
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const cancel = useMutation({
    mutationFn: () => cancelSubscription(),
    onSuccess: () => {
      toast.success('Авто-продление отключено. Доступ сохранится до конца оплаченного периода.');
      qc.invalidateQueries({ queryKey: ['billing'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading || !data) return <Spinner label="Загружаем подписку…" />;

  const status = data.status;
  const m = meta[status];
  const Icon = m.icon;
  const isTrial = status === 'TRIAL';
  const endIso = isTrial ? data.trialEndsAt : data.currentPeriodEnd;
  const left = daysLeft(endIso);
  const price = rub(data.priceKop);

  const payLabel =
    status === 'ACTIVE' ? 'Продлить на месяц' : status === 'TRIAL' ? 'Оплатить подписку' : 'Возобновить подписку';

  return (
    <div>
      <PageHeader title="Подписка" subtitle="Доступ к магазину для заведений" />

      <div className="grid gap-4 md:grid-cols-[1.4fr_1fr]">
        <SectionCard title="Текущий статус">
          <div className="flex items-center gap-3">
            <span className={`badge ${m.cls}`}>
              <Icon size={13} /> {m.label}
            </span>
            {data.autoRenew && (
              <span className="text-xs text-ink/50">Авто-продление включено</span>
            )}
          </div>

          <dl className="mt-5 space-y-3 text-sm">
            <div className="flex justify-between border-b border-line pb-3">
              <dt className="text-ink/50">{isTrial ? 'Триал до' : 'Период оплачен до'}</dt>
              <dd className="font-medium text-ink">{fmtDate(endIso)}</dd>
            </div>
            {left != null && (
              <div className="flex justify-between border-b border-line pb-3">
                <dt className="text-ink/50">Осталось</dt>
                <dd className="font-medium text-ink">{left} дн.</dd>
              </div>
            )}
            <div className="flex justify-between">
              <dt className="text-ink/50">Тариф</dt>
              <dd className="font-medium text-ink">{price} / мес</dd>
            </div>
          </dl>

          {(isTrial || status === 'PAST_DUE') && (
            <div className="mt-4 rounded-lg bg-amber-50 text-amber-800 text-sm px-3 py-2.5">
              {status === 'PAST_DUE'
                ? 'Оплата просрочена — заведения не видят ваш каталог. Продлите подписку.'
                : left != null && left <= 3
                  ? `Пробный период заканчивается через ${left} дн. Оплатите подписку, чтобы не потерять доступ.`
                  : 'После пробного периода оплатите подписку, чтобы каталог оставался в витрине.'}
            </div>
          )}

          {data.autoRenew && (
            <button
              className="mt-4 inline-flex items-center gap-1.5 text-sm text-ink/55 hover:text-red-600 transition"
              onClick={() => cancel.mutate()}
              disabled={cancel.isPending}
            >
              <XCircle size={15} /> Отключить авто-продление
            </button>
          )}
        </SectionCard>

        <SectionCard title="Оплата">
          <div className="text-3xl font-semibold text-ink">
            {price}
            <span className="text-base font-normal text-ink/40"> / мес</span>
          </div>
          <ul className="mt-4 space-y-2 text-sm text-ink/70">
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-emerald-500 shrink-0" /> Витрина в каталоге для заведений</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-emerald-500 shrink-0" /> Неограниченные товары и категории</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-emerald-500 shrink-0" /> Приём заявок и аналитика</li>
          </ul>

          <button
            className="btn-primary w-full mt-5 justify-center disabled:opacity-50"
            onClick={() => pay.mutate()}
            disabled={pay.isPending || redirecting || !data.billingEnabled}
          >
            {pay.isPending || redirecting ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <CreditCard size={16} />
            )}
            {payLabel}
          </button>

          {!data.billingEnabled && (
            <p className="mt-3 text-xs text-ink/50 text-center">
              Онлайн-оплата подключается. Напишите в поддержку для активации.
            </p>
          )}
          {data.billingEnabled && (
            <p className="mt-3 text-xs text-ink/45 text-center">
              Оплата картой через ЮKassa. Спишется {price}, далее — автоматически каждый месяц.
            </p>
          )}
        </SectionCard>
      </div>

      {/* История платежей */}
      {data.payments.length > 0 && (
        <SectionCard title="История платежей" className="mt-4">
          <div className="divide-y divide-line -my-1">
            {data.payments.map((p) => {
              const b = PAYMENT_BADGE[p.status];
              return (
                <div key={p.id} className="flex items-center gap-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-ink">
                      {rub(p.amount)}
                      {p.method ? <span className="text-ink/45 font-normal"> · {p.method}</span> : null}
                    </div>
                    <div className="text-xs text-ink/45">
                      {fmtDateTime(p.paidAt ?? p.createdAt)}
                      {p.failureReason ? ` · ${p.failureReason}` : ''}
                    </div>
                  </div>
                  <span className={`badge ${b.cls} shrink-0`}>{b.label}</span>
                </div>
              );
            })}
          </div>
        </SectionCard>
      )}
    </div>
  );
}
